import { createSelector } from 'reselect';

import {
	AviaPassengerType,
	BaggageType,
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment,
	GdsServiceVipSubType,
	OrderAdditionalServiceGdsServiceServiceType,
	TravellerGdsServiceService,
	TravellerSelectedSeat,
	TravellerSelectedServices
} from '@websky/graphql';
import { CheckoutState } from '../reducers';
import { SelectedSeatsServiceType, SelectedServicesState, SelectedServiceType } from './reducers';
import { PassengerServices, PassengerState } from '../passengers/reducers';
import { getSeatPrice } from '../../../SeatMap/utils';
import { getLinkedTravellersMap } from '../../../utils';
import { ServicePayload } from './actions';
import { LoadingServicesParamsState } from '../loadingServicesParams/reducers';

const getOrderState = (state: CheckoutState) => state.order;

const getPassengersState = (state: CheckoutState) => state.passengers;

export const getSelectedServicesState = (state: CheckoutState) => state.selectedServices;

export const getSelectedServicesTypes = (payload: ServicePayload | ServicePayload[]) =>
	createSelector(
		getOrderState,
		(order): LoadingServicesParamsState => {
			const gdsServicesMap = new Map(
				order.additionalServices?.gdsServices?.services?.map(service => [service.id, service]) ?? []
			);

			if (!gdsServicesMap.size) {
				return { id: [], rfisc: [], subType: [], type: [] };
			}

			const idSet = new Set<string>();
			const rfiscSet = new Set<string>();
			const typeSet = new Set<OrderAdditionalServiceGdsServiceServiceType>();
			const subTypeSet = new Set<BaggageType | GdsServiceVipSubType>();

			const payloadServices = Array.isArray(payload) ? payload : [payload];

			payloadServices.forEach(selectedService => {
				let rfisc: string;
				let type: OrderAdditionalServiceGdsServiceServiceType;
				let subType: BaggageType | GdsServiceVipSubType;

				const serviceId = selectedService?.serviceId ?? selectedService?.service?.id;
				const gdsService = gdsServicesMap.get(serviceId);

				if (gdsService) {
					rfisc = gdsService.rfisc;
					type = gdsService.type;

					if ((gdsService as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment)?.baggageType) {
						subType = (gdsService as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment)
							.baggageType;
					} else if ((gdsService as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment).subType) {
						subType = (gdsService as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment).subType;
					}
				} else if (selectedService?.service?.type) {
					type = selectedService?.service?.type;
				}

				if (serviceId) {
					idSet.add(serviceId);
				}

				if (rfisc) {
					rfiscSet.add(rfisc);
				}

				if (type) {
					typeSet.add(type);
				}

				if (subType) {
					subTypeSet.add(subType);
				}
			});

			return {
				id: [...idSet],
				rfisc: [...rfiscSet],
				subType: [...subTypeSet],
				type: [...typeSet]
			};
		}
	);

export const getSelectedServices = createSelector(
	[getSelectedServicesState, getPassengersState],
	(state: SelectedServicesState, passengers: PassengerState[]) => {
		const passengersServices: TravellerSelectedServices[] = [];

		passengers.forEach(passenger => {
			if (state[passenger.id]) {
				const passengerServices = state[passenger.id];

				const seatsServices: TravellerSelectedSeat[] = [],
					gdsServices: TravellerGdsServiceService[] = [];

				for (const serviceId in passengerServices) {
					if (passengerServices.hasOwnProperty(serviceId) && passengerServices[serviceId] !== null) {
						const services = passengerServices[serviceId].filter(service => service.number > 0);

						services.forEach(service => {
							switch (service.servicePayload?.type) {
								case OrderAdditionalServiceGdsServiceServiceType.Seat:
									const seatServicePayload: SelectedSeatsServiceType = service.servicePayload as SelectedSeatsServiceType;
									service.segments.forEach(segmentId => {
										seatsServices.push({
											segmentId,
											deckId: seatServicePayload.deckId,
											letter: seatServicePayload.letter,
											row: parseInt(seatServicePayload.row),
											type: seatServicePayload.seatType
										});
									});
									break;

								default:
									gdsServices.push({
										segmentIds: service.segments,
										serviceId: serviceId,
										count: service.number,
										extraValues: service.extraValues
									});
									break;
							}
						});
					}
				}

				passengersServices.push({
					travellerId: passenger.id,
					gdsServices: {
						setServices: gdsServices
					},
					seats: seatsServices
				});
			}
		});

		return passengersServices;
	}
);

export const getHasSelectedSeats = createSelector(getSelectedServices, selectedServices =>
	selectedServices?.some(travellerServices => travellerServices.seats?.length > 0)
);

export const getPassengerStateCompatible = createSelector(
	[getPassengersState, getSelectedServicesState],
	(passengers, selectedServices) => {
		const linkedPassengersIdsMap = getLinkedTravellersMap(passengers);

		return passengers.map<PassengerState>(passenger => {
			const passengerServices = selectedServices[passenger.id];

			const passengersServices: PassengerServices[] = [];
			const seats: SelectedSeatsServiceType[] = [];

			for (const serviceId in passengerServices) {
				if (passengerServices.hasOwnProperty(serviceId) && passengerServices[serviceId]) {
					const services = passengerServices[serviceId];

					services.forEach(service => {
						if (service.number > 0) {
							switch (service.servicePayload?.type) {
								case OrderAdditionalServiceGdsServiceServiceType.Seat:
									const seatServicePayload: SelectedSeatsServiceType = service.servicePayload as SelectedSeatsServiceType;
									const price = getSeatPrice(seatServicePayload, passenger.id);

									service.segments.forEach(segmentId => {
										seats.push({
											...seatServicePayload,
											price,
											segmentId
										});
									});
									break;

								case OrderAdditionalServiceGdsServiceServiceType.Baggage:
								case OrderAdditionalServiceGdsServiceServiceType.Meal:
									passengersServices.push({
										segmentIds: service.segments,
										serviceId: serviceId,
										count: service.number,
										confirmedCount: (service.servicePayload as SelectedServiceType).confirmedCount
									});
									break;

								case OrderAdditionalServiceGdsServiceServiceType.Vip:
									passengersServices.push({
										segmentIds: service.segments,
										serviceId: serviceId,
										count: service.number,
										confirmedCount: (service.servicePayload as SelectedServiceType).confirmedCount
									});
							}
						}
					});
				}
			}

			return {
				id: passenger.id,
				type: passenger.type,
				values: passenger.values,
				services: passengersServices,
				seatRestrictions: passenger.seatRestrictions,
				infantPassengerId:
					passenger.type === AviaPassengerType.ADT
						? linkedPassengersIdsMap.get(passenger.id)?.[0]
						: undefined,
				linkedTravellerIds: linkedPassengersIdsMap.get(passenger.id) ?? [],
				supplierTravellerType: passenger.supplierTravellerType,
				seats
			};
		});
	}
);
